import { RestrictiveIcon, Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import type { RestrictivePractice } from '@lmc/validations'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import StepEntryCard, {
  StepEntryCardItem,
  StepEntryCardRow
} from '../../StepEntryCard'
import { TIME_FORMAT } from '../../stepLayout'
import {
  INTERVENTION_TYPE_KEYS,
  keyFor,
  METHOD_KEYS,
  OUTCOME_KEYS,
  PRACTICE_TYPE_KEYS
} from './restrictivePracticeOptions'

type RestrictivePracticeCardProps = {
  onDelete: () => void
  onEdit: () => void
  practice: RestrictivePractice
  testId: string
}

/** One recorded practice: what was used, and Edit and Delete. */
const RestrictivePracticeCard = ({
  onDelete,
  onEdit,
  practice,
  testId
}: RestrictivePracticeCardProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const time = (iso: string) => dayjs(iso).format(TIME_FORMAT)
  const title = t(keyFor(PRACTICE_TYPE_KEYS, practice.type))

  const duration =
    practice.startedAt && practice.endedAt
      ? t(incidentReportsConstants.CASE_V2_RESTRICTIVE_DURATION_RANGE, {
          end: time(practice.endedAt),
          start: time(practice.startedAt)
        })
      : practice.startedAt && time(practice.startedAt)

  return (
    <StepEntryCard
      deleteLabel={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_DELETE, {
        practice: title
      })}
      editLabel={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_EDIT, {
        practice: title
      })}
      icon={<RestrictiveIcon size={24} />}
      onDelete={onDelete}
      onEdit={onEdit}
      testId={testId}
      title={title}
    >
      {(practice.method || duration) && (
        <StepEntryCardRow>
          {practice.method && (
            <StepEntryCardItem
              label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_METHOD)}
            >
              {t(keyFor(METHOD_KEYS, practice.method))}
            </StepEntryCardItem>
          )}
          {duration && (
            <StepEntryCardItem
              label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_DURATION)}
            >
              {duration}
            </StepEntryCardItem>
          )}
        </StepEntryCardRow>
      )}
      {practice.reasonForUse && (
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_REASON)}
        >
          {practice.reasonForUse}
        </StepEntryCardItem>
      )}
      {practice.deEscalationAttempted !== undefined && (
        <StepEntryCardItem
          label={t(
            incidentReportsConstants.CASE_V2_RESTRICTIVE_DE_ESCALATION_SUMMARY
          )}
        >
          {t(
            practice.deEscalationAttempted
              ? incidentReportsConstants.CASE_V2_YES
              : incidentReportsConstants.CASE_V2_NO
          )}
        </StepEntryCardItem>
      )}
      {practice.interventionType && (
        <StepEntryCardItem
          label={t(
            incidentReportsConstants.CASE_V2_RESTRICTIVE_INTERVENTION_TYPE
          )}
        >
          {t(keyFor(INTERVENTION_TYPE_KEYS, practice.interventionType))}
        </StepEntryCardItem>
      )}
      {practice.outcomes?.length ? (
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_OUTCOME)}
        >
          <Outcomes>
            {practice.outcomes.map((outcome) => (
              <Text key={outcome} as="li" weight="medium">
                {t(keyFor(OUTCOME_KEYS, outcome))}
              </Text>
            ))}
          </Outcomes>
        </StepEntryCardItem>
      ) : null}
      {practice.notes && (
        <StepEntryCardItem
          label={t(incidentReportsConstants.CASE_V2_RESTRICTIVE_NOTES)}
        >
          {practice.notes}
        </StepEntryCardItem>
      )}
    </StepEntryCard>
  )
}

const { spacing } = cssVars

const Outcomes = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing[100]};
  margin: 0;
  padding-inline-start: ${spacing[300]};
  list-style: disc;
`

export default RestrictivePracticeCard
